/**
 * Karbon360° — application shell.
 *
 * PHASE 1 of the v2 redesign. Rather than converting 15 page files to a
 * react-router layout route (21 `<Shell>` call sites, several inside
 * loading/error render branches), the shell itself was replaced. Every page
 * already renders `<Shell sidebar={<Sidebar/>}>`, so swapping this one component
 * gives the whole app the new chrome with no page edits and almost no blast
 * radius. Those pages are rewritten in Phase 3 anyway; the layout route lands
 * with them.
 *
 * The legacy `sidebar` prop is accepted and intentionally ignored — the new
 * sidebar is route-driven and needs no injection. `<Navbar/>` still appears as
 * the first child of most pages and now renders null (see Navbar.js), because
 * the topbar lives here.
 *
 * `.content` is preserved as the direct parent of page children: App.css:253
 * (`.content > *:not(...)`) is what gives every page its padding.
 */
import React, { useCallback, useEffect, useState } from 'react';
import SidebarV2 from './v2/SidebarV2';
import TopbarV2 from './v2/TopbarV2';
import CommandPalette from './v2/CommandPalette';
import { ReportingPeriodProvider } from './v2/ReportingPeriod';
import { ToastProvider } from './v2/ui';
import { X } from './v2/Icons';
import '../styles/karbon-v2.css';

const RAIL_KEY = 'k360-sidebar-rail';

export const Shell = ({ children }) => {
  const [rail, setRail] = useState(() => {
    try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(RAIL_KEY, rail ? '1' : '0'); } catch { /* ignore */ }
  }, [rail]);

  // ⌘K / Ctrl-K toggles the palette from anywhere in the app.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const cls = ['k2-app', rail ? 'k2-rail' : '', mobileOpen ? 'k2-mobile-open' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <ReportingPeriodProvider>
      <ToastProvider>
      <button
        type="button"
        className="k2-burger"
        onClick={() => setMobileOpen((v) => !v)}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
      >
        {mobileOpen ? <X.Close s={17} /> : <X.Menu s={17} />}
      </button>

      <div className={cls}>
        <div className="k2-scrim" onClick={closeMobile} />

        <SidebarV2 rail={rail} setRail={setRail} onNavigate={closeMobile} />

        <div className="k2-main">
          <TopbarV2 onOpenPalette={() => setPaletteOpen(true)} />
          <div className="k2-scroll">
            <div className="content">{children}</div>
          </div>
        </div>
      </div>

      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
      </ToastProvider>
    </ReportingPeriodProvider>
  );
};

export default Shell;
